import { useEffect, useState } from "react";

import { Button } from "src/components/buttons";
import { ApiError } from "src/core/apiError";

import { CandidateChangesDrawer } from "./candidateChangesDrawer";
import { BaseCandidate } from "./candidateTypes";

import "./previewChanges.scss";

interface PreviewChangesDrawerProps<T extends BaseCandidate> {
  readonly onClose: () => void;
  readonly fetchCandidates: () => Promise<T[] | undefined>;
  readonly onReviewClick: () => void;
  readonly contextId?: string;
  readonly refreshSignal?: unknown;
}

export const PreviewChangesDrawer = <T extends BaseCandidate>({
  onClose,
  fetchCandidates,
  onReviewClick,
  contextId,
  refreshSignal
}: PreviewChangesDrawerProps<T>) => {
  const [candidate, setCandidate] = useState<T | undefined>();
  const [candidateLoading, setCandidateLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | undefined>();

  useEffect(() => {
    // This effect owns one candidate request. Cleanup marks it inactive when
    // the drawer closes or a newer refresh starts so an older response cannot
    // overwrite the latest candidate, error, or loading state.
    let active = true;
    /**
     * Loads all the pending transactions.
     * there should just be one with a bunch of
     * events
     */
    if (contextId) {
      setCandidateLoading(true);
      setError(undefined);
      fetchCandidates()
        .then(
          (candidates: T[] = []) => {
            if (active) {
              setCandidate(candidates[0]);
            }
          },
          (err: ApiError) => {
            if (active) {
              setError(err);
            }
          }
        )
        .finally(() => {
          if (active) {
            setCandidateLoading(false);
          }
        });
    }
    return () => {
      active = false;
    };
  }, [contextId, fetchCandidates, refreshSignal]);

  return (
    <CandidateChangesDrawer
      title="Preview changes"
      onClose={onClose}
      candidate={candidate}
      error={error}
      isOpen={true}
      loading={candidateLoading}
    >
      <Button name="review-config" kind="primary" onClick={onReviewClick}>
        Review configuration
      </Button>
    </CandidateChangesDrawer>
  );
};
